import React, { useState, useEffect } from 'react';
import { 
  LineChart, Line, ResponsiveContainer, BarChart, Bar, 
  PieChart, Pie, Cell 
} from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import { 
  FaFileExcel, FaFilePdf, FaSyncAlt, FaFilter, 
  FaStar, FaRegStar, FaHistory, FaCheckCircle, FaDownload,
  FaSearch, FaUserFriends, FaIdBadge
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

import "react-datepicker/dist/react-datepicker.css";

// --- Theme Config ---
const COLORS = {
  primary: '#3B82F6',
  teal: '#0D9488',
  purple: '#8B5CF6',
  green: '#22C55E',
  orange: '#F97316', 
  slate: '#64748b'
};

const ReportCenter = () => {
  const [isGenerating, setIsGenerating] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [favorites, setFavorites] = useState([1, 4]);
  const [history, setHistory] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('Daily');
  const [searchId, setSearchId] = useState('');

  const periods = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Half Yearly', 'Yearly', 'Custom'];

  const reports = [
    { id: 1, title: ' Attendance Report', desc: 'Check-in/out logs & late counts', coverage: '1,240 workers', type: 'Daily', chart: 'bar' },
    { id: 2, title: 'Monthly Delivery Report', desc: 'On-time vs Delayed dispatch trends', coverage: '42 Active Sites', type: 'Monthly', chart: 'line' },
    { id: 3, title: 'Quarterly Productivity', desc: 'Efficiency rankings by department', coverage: '8 Departments', type: 'Quarterly', chart: 'pie' },
    { id: 4, title: 'Yearly CRM Summary', desc: 'Executive growth and retention data', coverage: 'Fiscal Year 2025', type: 'Yearly', chart: 'line' },
  ];

  const employees = [
    { id: 'INF001', name: 'Arjun Mehra', dept: 'Operations', status: 'Active' },
    { id: 'INF042', name: 'Saira Khan', dept: 'HR', status: 'Active' },
    { id: 'INF089', name: 'Mark Robinson', dept: 'Logistics', status: 'On Leave' },
    { id: 'INF112', name: 'Priya Das', dept: 'Finance', status: 'Active' },
    { id: 'INF204', name: 'Vikram Singh', dept: 'Tech', status: 'Active' },
  ];

  const filteredEmployees = employees.filter(emp => 
    emp.id.toLowerCase().includes(searchId.toLowerCase())
  );

  // --- Multi-Sheet Excel Export ---
  const handleExportAll = async () => {
    setIsGenerating('all');
    await new Promise(resolve => setTimeout(resolve, 2500));
    const wb = XLSX.utils.book_new();
    reports.forEach(report => {
      const data = [{ Report: report.title, Coverage: report.coverage, Type: selectedPeriod, ExportDate: new Date().toLocaleString() }];
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, report.id.toString());
    });
    XLSX.writeFile(wb, `Global_Report_${selectedPeriod}.xlsx`);
    setHistory(prev => [{ name: `Global ${selectedPeriod} Export`, time: 'Just now', format: 'excel' }, ...prev].slice(0, 5));
    setIsGenerating(null);
  };

  const handleExport = async (report, format) => {
    const id = `${report.id}-${format}`;
    setIsGenerating(id);
    await new Promise(resolve => setTimeout(resolve, 1800));
    if (format === 'excel') {
      const ws = XLSX.utils.json_to_sheet([{ Report: report.title, Period: selectedPeriod, Date: startDate.toDateString(), Scope: report.coverage }]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Data");
      XLSX.writeFile(wb, `${report.title.replace(/\s/g, '_')}.xlsx`);
    } else {
      const doc = new jsPDF();
      doc.setFont("helvetica", "bold");
      doc.text(report.title, 14, 20);
      doc.autoTable({
        startY: 30,
        head: [['Metric', 'Detail']],
        body: [['Period', selectedPeriod], ['Selected Date', startDate.toDateString()], ['Generated', new Date().toLocaleString()]],
        theme: 'striped'
      });
      doc.save(`${report.title.replace(/\s/g, '_')}.pdf`);
    }
    setHistory(prev => [{ name: report.title, time: 'Just now', format }, ...prev].slice(0, 5));
    setIsGenerating(null);
  };

  return (
    <div className="min-h-screen bg-white font-['Roboto'] text-slate-700">
      
      {/* 🔹 STICKY HEADER */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100 px-8 py-4 flex justify-between items-center">
        <div>
          <h1 className="font-['Poppins'] text-xl font-bold text-slate-900">Reports Center</h1>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Global Analytics Management</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Period Dropdown Selection */}
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-full px-4 py-1.5 shadow-sm">
            <span className="text-[9px] font-black text-slate-400 uppercase">View:</span>
            <select 
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="bg-transparent text-[11px] font-bold text-slate-700 outline-none cursor-pointer border-r border-slate-200 pr-2 mr-1"
            >
              {periods.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <DatePicker selected={startDate} onChange={d => setStartDate(d)} className="bg-transparent text-[11px] font-bold text-blue-600 outline-none w-24 cursor-pointer" />
          </div>

          <button 
            onClick={handleExportAll}
            disabled={isGenerating === 'all'}
            className="flex items-center gap-2 bg-orange-500 text-white text-[10px] font-black uppercase tracking-widest px-6 py-2.5 rounded-full shadow-lg shadow-orange-100 hover:bg-orange-600 transition-all active:scale-95 disabled:opacity-50"
          >
            {isGenerating === 'all' ? <FaSyncAlt className="animate-spin" /> : <FaFileExcel />}
            {isGenerating === 'all' ? 'Bundling...' : 'Global Export'}
          </button>
        </div>
      </header>

      <main className="p-8 max-w-7xl mx-auto">
        
        {/* 🔹 SUMMARY METRICS STRIP */}
        <section className="flex items-center gap-12 mb-10 py-4 border-b border-slate-50">
          <StatItem label="Total Employees" val="1,240" icon={<FaUserFriends className="text-blue-500"/>} />
          <StatItem label="Active Period" val={selectedPeriod} icon={<FaHistory className="text-purple-500"/>} />
          <StatItem label="Data Integrity" val="Stable" icon={<FaCheckCircle className="text-green-500"/>} />
        </section>

        {/* 🔹 REPORT GRID */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-['Poppins'] text-xs font-bold uppercase tracking-widest text-slate-900 flex items-center gap-2">
              <span className="w-2 h-2 bg-indigo-500 rounded-full"></span> Analytical Modules
            </h2>
            <button className="text-slate-400 hover:text-primary transition-colors"><FaFilter size={14}/></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-slate-100 rounded-xl overflow-hidden shadow-sm">
            {reports.map((report) => (
              <ReportTile 
                key={report.id} 
                report={report} 
                isFav={favorites.includes(report.id)}
                onFav={() => setFavorites(f => f.includes(report.id) ? f.filter(i => i !== report.id) : [...f, report.id])}
                onExport={handleExport}
                isGenerating={isGenerating}
              />
            ))}
          </div>
        </section>

        {/* 🔹 OVERALL EMPLOYEES SECTION */}
        <section className="bg-slate-50/50 rounded-3xl p-8 border border-slate-100">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <div>
              <h2 className="font-['Poppins'] text-lg font-bold text-slate-900">Employee Directory</h2>
              <p className="text-xs text-slate-400">Manage and track individual employee reporting IDs</p>
            </div>
            
            {/* ID Search Bar */}
            <div className="relative group">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-blue-500 transition-colors" size={12}/>
              <input 
                type="text" 
                placeholder="Search Employee ID..." 
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                className="pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all w-64 shadow-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {filteredEmployees.map((emp) => (
              <div key={emp.id} className="bg-white p-4 rounded-2xl border border-slate-100 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-500/5 transition-all group cursor-pointer">
                <div className="flex items-center gap-3 mb-3">
                   <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                      <FaIdBadge size={14}/>
                   </div>
                   <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">{emp.id}</span>
                </div>
                <h4 className="text-sm font-bold text-slate-800 mb-1">{emp.name}</h4>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-medium text-slate-400">{emp.dept}</span>
                  <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-md ${emp.status === 'Active' ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-600'}`}>
                    {emp.status}
                  </span>
                </div>
              </div>
            ))}
            {filteredEmployees.length === 0 && (
              <div className="col-span-full py-12 text-center text-slate-400 text-xs font-medium">No employees found with ID "{searchId}"</div>
            )}
          </div>
        </section>

        {/* 🕒 RECENT HISTORY PANEL */}
        <AnimatePresence>
          {history.length > 0 && (
            <motion.div initial={{ x: 300 }} animate={{ x: 0 }} exit={{ x: 300 }} className="fixed bottom-8 right-8 bg-slate-900 text-white p-5 rounded-2xl shadow-2xl w-64 z-[60]">
              <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-2">
                <FaHistory className="text-blue-400" size={12}/>
                <span className="text-[10px] font-black uppercase tracking-widest">Recent Activity</span>
              </div>
              {history.map((h, i) => (
                <div key={i} className="flex justify-between items-center text-[10px] mb-2 opacity-80">
                  <span className="truncate w-32 font-medium">{h.name}</span>
                  <span className="uppercase text-[8px] bg-white/10 px-1 rounded">{h.format}</span>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

// --- Sub-Components ---

const StatItem = ({ label, val, icon }) => (
  <div className="flex items-center gap-3">
    <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">{icon}</div>
    <div>
      <p className="text-[9px] font-black uppercase text-slate-400 tracking-tighter leading-none mb-1.5">{label}</p>
      <p className="text-sm font-bold text-slate-900 leading-none">{val}</p>
    </div>
  </div>
);

const ReportTile = ({ report, isFav, onFav, onExport, isGenerating }) => (
  <div className="group border-r border-b border-slate-100 p-8 flex flex-col lg:flex-row items-center gap-8 transition-all hover:bg-slate-50/50 bg-white">
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onFav} className={`transition-colors ${isFav ? 'text-amber-400' : 'text-slate-200 hover:text-slate-400'}`}>
          {isFav ? <FaStar /> : <FaRegStar />}
        </button>
        <span className="text-[9px] font-black text-primary uppercase tracking-widest">{report.type}</span>
      </div>
      <h4 className="font-['Poppins'] text-sm font-semibold text-slate-800 truncate">{report.title}</h4>
      <p className="text-xs text-slate-400 mt-1 line-clamp-1">{report.desc}</p>
      <p className="text-[10px] font-bold text-slate-500 mt-3 flex items-center gap-1">
        <FaCheckCircle className="text-green-500" size={10}/> {report.coverage}
      </p>
    </div>

    <div className="w-24 h-12 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
      <ResponsiveContainer width="100%" height="100%">
        {report.chart === 'line' ? (
          <LineChart data={[{v:10},{v:25},{v:15},{v:35}]}>
            <Line type="monotone" dataKey="v" stroke={COLORS.primary} strokeWidth={2} dot={false} />
          </LineChart>
        ) : report.chart === 'bar' ? (
          <BarChart data={[{v:40},{v:70},{v:50},{v:90}]}>
            <Bar dataKey="v" fill={COLORS.teal} radius={[2, 2, 0, 0]} />
          </BarChart>
        ) : (
          <PieChart>
            <Pie data={[{v:30},{v:70}]} innerRadius={12} outerRadius={22} dataKey="v" stroke="none">
              <Cell fill={COLORS.purple} />
              <Cell fill="#f1f5f9" />
            </Pie>
          </PieChart>
        )}
      </ResponsiveContainer>
    </div>

    <div className="flex flex-col gap-2 flex-shrink-0">
      <ExportBtn format="excel" loading={isGenerating === `${report.id}-excel`} onClick={() => onExport(report, 'excel')} />
      <ExportBtn format="pdf" loading={isGenerating === `${report.id}-pdf`} onClick={() => onExport(report, 'pdf')} />
    </div>
  </div>
);

const ExportBtn = ({ format, loading, onClick }) => (
  <button 
    onClick={onClick}
    disabled={loading}
    className={`flex items-center justify-center gap-2 w-28 py-2 rounded-full text-[9px] font-black uppercase tracking-widest border transition-all
      ${format === 'excel' ? 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-600 hover:text-white' : 
                          'bg-rose-50 text-rose-700 border-rose-100 hover:bg-rose-600 hover:text-white'}`}
  >
    {loading ? <FaSyncAlt className="animate-spin text-xs" /> : (format === 'excel' ? <FaFileExcel /> : <FaFilePdf />)}
    {loading ? 'Processing' : format}
  </button>
);

export default ReportCenter;